/**
 * O que nunca sai da máquina por uma conversa.
 *
 * Esta lista existe para UMA porta: a sessão mestre enviando arquivo para o
 * painel. Ela é mais dura que a `NEGADOS` do daemon (`apps/agent/src/fs/
 * allowlist.ts`) de propósito, e as duas não foram fundidas de propósito
 * também — a do daemon governa o EDITOR, onde negar `settings.json` tiraria da
 * pessoa um arquivo que ela edita a mão; esta governa a saída de bytes para o
 * armazenamento, onde não existe caso legítimo nenhum.
 *
 * Por que a régua aqui é mais alta que em qualquer outro lugar do projeto:
 *
 * 1. **O que passa por uma conversa FICA.** Vira linha em `eventos_sessao`,
 *    que é append-only, e volta pela busca em português meses depois. Um
 *    segredo que sai por aqui não sai uma vez: ele fica guardado, indexado e
 *    recuperável.
 * 2. **A mestre roda em modo `auto`, onde não existe cartão de permissão.**
 *    Não há humano no laço para barrar a chamada. O que este arquivo recusar é
 *    literalmente tudo que existe entre uma injeção bem-sucedida e o segredo
 *    no armazenamento.
 * 3. Por isso **não há escotilha** — nem atrás de flag, nem atrás de ADMIN.
 *    Uma porta condicional é a porta que o atacante procura: ele não precisa
 *    convencer o dono, precisa convencer o classificador uma vez.
 *
 * Falso positivo aqui custa uma mensagem de recusa; falso negativo custa a
 * chave privada. A lista erra acusando.
 */

/**
 * Pastas que nunca saem inteiras, em qualquer nível do caminho.
 *
 * Negar a pasta é mais forte que enumerar os arquivos dela: `~/.ssh` ganha
 * nomes novos (`id_ed25519_sk`, `config.d/`) sem ninguém avisar esta lista.
 */
const PASTAS_DE_SEGREDO: RegExp[] = [
  /(^|\/)\.ssh(\/|$)/i,
  /(^|\/)\.aws(\/|$)/i,
  /(^|\/)\.gnupg(\/|$)/i,
  /(^|\/)\.kube(\/|$)/i,
  /(^|\/)\.docker(\/|$)/i,
  /(^|\/)\.git(\/|$)/i,
  // `.claude`, `.claude-mestre`, `.claude-trabalho`: é onde mora o
  // `.credentials.json` de cada perfil do Claude Code.
  /(^|\/)\.claude[^/]*(\/|$)/i,
];

/**
 * Nomes de arquivo que nunca saem, comparados contra o último pedaço do
 * caminho.
 */
const NOMES_DE_SEGREDO: RegExp[] = [
  /*
    Ambiente, em três formas. A terceira é a que a frota já pagou: existiu um
    `.env.bak-<data>` numa máquina, e um casamento por extensão exata
    (`.endsWith('.env')`) passaria por cima dele. Aqui `.env` seguido de ponto
    OU hífen é recusado, em qualquer posição do nome.
  */
  /\.env$/i,
  /\.env[.-]/i,
  /^env$/i,

  // Credenciais nomeadas.
  /^\.credentials\.json$/i,
  /^\.claude\.json$/i,
  /^credentials(\.json)?$/i,
  /^\.git-credentials$/i,
  /^\.netrc$/i,
  /^_netrc$/i,
  /^\.pgpass$/i,
  /^\.npmrc$/i,
  /^\.htpasswd$/i,

  /*
    `settings.json` do Claude Code carrega um segredo real gravado por nós: o
    `env.REMOTO_HOOK_TOKEN`. Existe um redator dedicado a ele em
    `apps/agent/src/fs/redigir.ts`, o que é a prova de que o arquivo é
    perigoso — mas o redator só é usado na leitura curada do `.claude`, e não
    teria como agir sobre bytes que sobem inteiros para o armazenamento.
  */
  /^settings\.json$/i,
  /^\.mcp\.json$/i,

  // Chaves de SSH, com e sem token físico.
  /^id_[a-z0-9]+(_sk)?$/i,
  /^authorized_keys$/i,
  /^known_hosts$/i,

  // Material criptográfico por extensão.
  /\.(pem|key|pfx|p12|p8|jks|keystore|kdbx|ppk|asc|gpg|ovpn)$/i,

  // O cofre do toolkit (F3.4, D33): os segredos das rotinas, cifrados pela
  // DPAPI no PC e `root:claude 640` na VPS. Os nomes são os de
  // `NOMES_DO_COFRE_DO_TOOLKIT`, logo abaixo.
  /^cofre\.dat$/i,
  /^toolkit-cofre\.json$/i,
];

/**
 * Os arquivos do cofre do toolkit (`apps/agent/src/toolkit/cofre.ts`): no PC,
 * `%LOCALAPPDATA%/Remoto/toolkit/cofre.dat`; na VPS,
 * `/etc/remoto-agent/toolkit-cofre.json`. A fonte única dos NOMES que a guarda
 * dos motores procura (o gancho do Claude, as regras do OpenCode) e que o
 * `pareceSegredo` recusa.
 */
export const NOMES_DO_COFRE_DO_TOOLKIT = Object.freeze(['cofre.dat', 'toolkit-cofre.json'] as const);

/**
 * `true` quando QUALQUER texto dentro de `valor` (a entrada de uma ferramenta,
 * de qualquer forma: caminho, comando, padrão de busca) cita o cofre.
 *
 * Erra acusando de propósito, como toda guarda da casa: um `cat` que mencione
 * `cofre.dat` num comentário é recusado. E **não fecha**: a sessão roda como o
 * mesmo usuário do daemon, e um comando que monte o nome em pedaços passa (D33,
 * "mitiga e não fecha", vai no ADR-0026).
 */
export function citaOCofre(valor: unknown): boolean {
  if (typeof valor === 'string') {
    const texto = valor.toLowerCase();
    return NOMES_DO_COFRE_DO_TOOLKIT.some((nome) => texto.includes(nome));
  }
  if (Array.isArray(valor)) return valor.some(citaOCofre);
  if (typeof valor === 'object' && valor !== null) return Object.values(valor).some(citaOCofre);
  return false;
}

/**
 * `true` quando o caminho aponta para algo que não pode sair da máquina.
 *
 * Função pura sobre string: quem toca o disco é o chamador, e é ele que já
 * resolveu o caminho com `realpath` antes de perguntar. Perguntar antes de
 * resolver deixaria um symlink `foto.png -> ~/.ssh/id_rsa` passar.
 */
export function pareceSegredo(caminho: string): boolean {
  const normalizado = caminho.replace(/\\/g, '/');
  if (PASTAS_DE_SEGREDO.some((regra) => regra.test(normalizado))) return true;

  const nome = normalizado.split('/').pop() ?? '';
  return NOMES_DE_SEGREDO.some((regra) => regra.test(nome));
}
